import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button, InternalHookFormInput } from '../../../atoms';
import { EditCallCentreProfileFormFields } from '../../../../types';
import { editCallCentreProfileFormValidationSchema } from '../../../../validators';

const INPUT_CLASSES =
  'w-full rounded-md border-gray-300 focus:border-secondary px-3 py-1.5 text-sm outline-none';

type FieldProps = {
  label: string;
  children: React.ReactNode;
};

const Field: React.FC<FieldProps> = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xxs font-semibold uppercase tracking-wider text-text-grey">{label}</span>
    {children}
  </label>
);

type EditCallCentreProfileFormProps = {
  defaultValues: EditCallCentreProfileFormFields;
  onSubmit: (values: EditCallCentreProfileFormFields) => Promise<void>;
  onDelete: () => void;
  isSubmitLoading: boolean;
  displayDelete: boolean;
  isCallActive: boolean;
};

export const EditCallCentreProfileForm: React.FC<EditCallCentreProfileFormProps> = ({
  defaultValues,
  onSubmit,
  onDelete,
  isSubmitLoading,
  displayDelete,
  isCallActive,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    reset,
  } = useForm({
    defaultValues,
    resolver: yupResolver(editCallCentreProfileFormValidationSchema),
    mode: 'onTouched',
  });

  const _navigateToCallContactProfile = () => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const baseUrl = `${url.protocol}//${url.host}/`;
    window.open(`${baseUrl}admin/individual/user?Id=${defaultValues.userId}`, '_blank');
  };

  const _callContact = () => {
    const cellphoneNumber = getValues('cellphoneNumber')?.replace(/[^\d+]/g, '');

    if (!cellphoneNumber) return;

    window.location.href = `tel:${cellphoneNumber}`;
  };

  const submit = async () => {
    await onSubmit(getValues() as EditCallCentreProfileFormFields);
    reset({}, { keepValues: true });
  };

  return (
    <form className="flex flex-col flex-grow gap-3 p-4">
      <InternalHookFormInput variableName="userId" register={register} hidden />
      <Field label="First Name">
        <InternalHookFormInput
          className={INPUT_CLASSES}
          variableName="firstName"
          register={register}
          errors={errors}
        />
      </Field>
      <Field label="Last Name">
        <InternalHookFormInput
          className={INPUT_CLASSES}
          variableName="lastName"
          register={register}
          errors={errors}
        />
      </Field>
      <Field label="Cell">
        <div className="flex flex-row items-start gap-2">
          <div className="flex flex-col flex-grow">
            <InternalHookFormInput
              className={INPUT_CLASSES}
              variableName="cellphoneNumber"
              errors={errors}
              register={register}
            />
          </div>
          <Button
            onClick={_callContact}
            className="h-9 min-h-9 py-0 bg-secondary hover:bg-secondary-dark disabled:bg-grey-brand-3 disabled:text-text-grey text-white text-xs font-semibold px-4 rounded-md"
            disabled={!isCallActive}
          >
            Call
          </Button>
        </div>
      </Field>
      <Field label="Email">
        <InternalHookFormInput
          className={INPUT_CLASSES}
          variableName="email"
          register={register}
          errors={errors}
        />
      </Field>
      <Field label="Partner">
        <InternalHookFormInput
          className={`${INPUT_CLASSES} bg-grey-brand-1 text-text-grey`}
          variableName="partner"
          register={register}
          disabled
        />
      </Field>
      <div className="mt-auto -mx-4 flex flex-wrap items-center gap-2 border-t border-grey-brand-1 px-4 pt-3">
        <Button
          isLoading={isSubmitLoading}
          onClick={handleSubmit(submit)}
          className="bg-primary hover:bg-primary-dark text-white text-xs font-semibold px-4 rounded-md"
          spinnerSize={10.5}
          disabled={!isDirty}
        >
          <span>Update</span>
        </Button>
        <Button
          type="text"
          onClick={_navigateToCallContactProfile}
          className="text-secondary hover:bg-grey-brand-1 text-xs font-semibold px-3 rounded-md"
        >
          Open Profile on Portal
        </Button>
        {displayDelete && (
          <Button
            onClick={onDelete}
            className="bg-tertiary hover:bg-tertiary-dark text-white text-xs font-semibold px-4 ml-auto rounded-md"
            spinnerSize={10.5}
          >
            Delete
          </Button>
        )}
      </div>
    </form>
  );
};
